import { Component, inject, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { firstValueFrom, Observable, map, startWith } from 'rxjs';
import { Persona } from 'src/app/models/personas.interface';
import { ReviewSession } from 'src/app/models/review.interface';
import { Theme } from 'src/app/models/theme.interface';
import { PersonaService } from 'src/app/services/persona.service';
import { ReviewService } from 'src/app/services/review.service';
import { Strings } from 'src/app/shared/strings';
import { FormControl } from '@angular/forms';

@Component({
  selector: 'app-reviewers-details',
  templateUrl: './reviewers.details.component.html',
  styleUrls: ['./reviewers.details.component.scss']
})
export class ReviewersDetailsComponent implements OnInit {
  currentDate = new Date().toLocaleDateString();
  personas!: Persona[];
  cols = 3;
  strings = Strings;
  selectedPersona!: Persona;
  reviewerSession!: ReviewSession;
  _snackBar = inject(MatSnackBar);

  // Theme filtering
  themeFilterControl = new FormControl('');
  filteredThemes: Observable<Theme[]> | undefined;
  allThemes: Theme[] = [];

  constructor(
    private router: Router,
    private reviewerService: ReviewService,
    private personaService: PersonaService
  ) {}

  async ngOnInit() {
    const reviewer_id = this.router.url.split('/').pop();
    if (!reviewer_id) {
      console.error(`Invalid reviewer ID ${reviewer_id} provided`);
      this.router.navigate(['/reviewers']);
      return;
    }

    const reviewerSession = await firstValueFrom(this.reviewerService.getReviewSession(reviewer_id));
    const persona = await firstValueFrom(this.personaService.getPersona(reviewerSession.persona_id));
    this.selectedPersona = persona;
    this.reviewerSession = reviewerSession;

    // Setup theme filtering
    this.allThemes = this.reviewerSession.themes;
    this.setupThemeFiltering();

    console.log(this.selectedPersona);
  }

  setupThemeFiltering() {
    this.filteredThemes = this.themeFilterControl.valueChanges.pipe(
      startWith(''),
      map(value => this._filterThemes(value || ''))
    );
  }

  private _filterThemes(value: string): Theme[] {
    const filterValue = value.toLowerCase();
    return this.allThemes.filter(theme => theme.theme.toLowerCase().includes(filterValue));
  }

  clearThemeFilter() {
    this.themeFilterControl.setValue('');
  }

  showView() {
    this.router.navigate(['/reviewers']);
  }

  toClipboard(text: string) {
    navigator.clipboard.writeText(text);
    this._snackBar.open('Copied to clipboard', 'Close', {
      duration: 2000
    });
  }

  toggleTheme(themeIdx: number, reviewIdx: number) {
    const resolvedStatus = !this.reviewerSession.themes[themeIdx].reviews.oppose[reviewIdx].resolved;
    const issueId = `${themeIdx + 1}-${reviewIdx + 1}`;
    this.reviewerSession.themes[themeIdx].reviews.oppose[reviewIdx].resolved = resolvedStatus;

    this.reviewerService.updateReviewSession(this.reviewerSession).subscribe({
      next: () => {
        this._snackBar.open(`Issue ${issueId} has been ${resolvedStatus ? 'resolved' : 'unresolved'}`, 'Close', {
          duration: 3000,
          panelClass: ['success-snackbar']
        });
      },
      error: error => {
        console.error('Error updating resolved status:', error);
        this._snackBar.open(`Error updating Issue ${issueId}`, 'Close', {
          duration: 9000,
          panelClass: ['error-snackbar']
        });
        this.reviewerSession.themes[themeIdx].reviews.oppose[reviewIdx].resolved = !resolvedStatus;
      }
    });
  }

  // TODO: Implement saveNotes?
  // saveNotes() {
  //   // Save to backend
  //   this.reviewerService.updateReviewSession(this.reviewerSession).subscribe({
  //     next: () => {
  //       this._snackBar.open('Notes saved successfully', 'Close', {
  //         duration: 3000,
  //         panelClass: ['success-snackbar']
  //       });
  //     },
  //     error: error => {
  //       console.error('Error saving notes:', error);
  //       this._snackBar.open('Error saving notes', 'Close', {
  //         duration: 9000,
  //         panelClass: ['error-snackbar']
  //       });
  //     }
  //   });
  // }

  getUnresolvedCount(): number {
    if (!this.reviewerSession?.themes) return 0;

    return this.reviewerSession.themes.reduce((count, theme) => {
      if (!theme.reviews?.oppose) return count;
      const unresolvedCount = theme.reviews.oppose.filter(review => review.resolved === false).length;
      return count + unresolvedCount;
    }, 0);
  }

  getResolvedCount(): number {
    if (!this.reviewerSession?.themes) return 0;

    return this.reviewerSession.themes.reduce((count, theme) => {
      if (!theme.reviews?.oppose) return count;
      const resolvedCount = theme.reviews.oppose.filter(review => review.resolved === true).length;
      return count + resolvedCount;
    }, 0);
  }

  getTotalCount(): number {
    if (!this.reviewerSession?.themes) return 0;

    return this.reviewerSession.themes.reduce((count, theme) => {
      if (!theme.reviews?.oppose) return count;
      return count + theme.reviews.oppose.length;
    }, 0);
  }
}
